import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiFetch } from "../utils/api";
import { Badge, estadoVisible, mapaEstadosSolicitud } from "../utils/estados";
import { Ayuda } from "../context/AyudaContext";
import Cargando from "../components/Cargando";

const PILLS_ESTADO = [
  { valor: "", texto: "Todas" },
  { valor: "entregada", texto: "✅ Entregadas" },
  { valor: "rechazada", texto: "❌ Rechazadas" },
];

const TAMANOS_PAGINA = ["10", "20", "50", "todas"];

function iniciales(nombre) {
  if (!nombre) return "?";
  const partes = nombre.trim().split(/\s+/);
  return ((partes[0]?.[0] || "") + (partes[1]?.[0] || "")).toUpperCase();
}

function Historico() {
  const usuario = JSON.parse(localStorage.getItem("usuario") || "null");
  const esStaff =
    usuario?.rol === "compras" || usuario?.rol === "aprobador" || usuario?.rol === "administrador";

  // Los tiles del Dashboard mandan aquí con ?estado=entregada o
  // ?estado=rechazada para llegar ya filtrado, en vez de mostrar las dos
  // cosas mezcladas.
  const [searchParams] = useSearchParams();
  const [pillEstado, setPillEstado] = useState(
    ["entregada", "rechazada"].includes(searchParams.get("estado")) ? searchParams.get("estado") : ""
  );

  const [solicitudes, setSolicitudes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

  const [folio, setFolio] = useState("");
  const [solicitanteNombre, setSolicitanteNombre] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  const [tamanoPagina, setTamanoPagina] = useState("10");
  const [pagina, setPagina] = useState(1);

  const cargar = () => {
    setCargando(true);
    setError("");
    // El histórico archiva lo entregado y lo rechazado — ambos salen del
    // Dashboard principal en cuanto llegan a uno de estos dos estados.
    const params = new URLSearchParams({ estado: "entregada,rechazada" });
    if (esStaff && solicitanteNombre) params.set("solicitante", solicitanteNombre);
    if (desde) params.set("desde", desde);
    if (hasta) params.set("hasta", hasta);

    const endpoint = esStaff ? "/api/solicitudes" : "/api/solicitudes/mias";
    apiFetch(`${endpoint}?${params.toString()}`)
      .then((data) => setSolicitudes(data?.items || []))
      .catch((err) => setError(err.message))
      .finally(() => setCargando(false));
  };

  useEffect(cargar, []);

  const buscar = (e) => {
    e.preventDefault();
    setPagina(1);
    cargar();
  };

  const cambiarPill = (valor) => {
    setPillEstado(valor);
    setPagina(1);
  };

  const visibles = solicitudes
    .filter((s) => !pillEstado || s.estado === pillEstado)
    .filter((s) => !folio || String(s.id).padStart(3, "0").includes(folio.replace("#", "")));

  const totalPaginas =
    tamanoPagina === "todas" ? 1 : Math.max(1, Math.ceil(visibles.length / Number(tamanoPagina)));
  const paginaSegura = Math.min(pagina, totalPaginas);
  const visiblesPagina =
    tamanoPagina === "todas"
      ? visibles
      : visibles.slice((paginaSegura - 1) * Number(tamanoPagina), paginaSegura * Number(tamanoPagina));

  // Tarjetas de resumen — sobre el total ya archivado (sin filtrar por folio
  // ni por la pestaña activa), para que siempre reflejen el panorama completo.
  const totalArchivadas = solicitudes.length;
  const totalEntregadas = solicitudes.filter((s) => s.estado === "entregada").length;
  const totalRechazadas = solicitudes.filter((s) => s.estado === "rechazada").length;
  const pct = (n) => (totalArchivadas ? Math.round((n / totalArchivadas) * 100) : 0);
  // El solicitante las recibió; el personal las entregó.
  const textoEntregadas = usuario?.rol === "solicitante" ? "Recibidas" : "Entregadas";

  const TARJETAS = [
    { texto: "Total archivadas", valor: totalArchivadas, icono: "🗄️", color: "#3d5a99", bg: "#e1e7f5" },
    { texto: textoEntregadas, valor: totalEntregadas, sub: `${pct(totalEntregadas)}%`, icono: "✅", color: "#2f7a54", bg: "#dcf0e6" },
    { texto: "Rechazadas", valor: totalRechazadas, sub: `${pct(totalRechazadas)}%`, icono: "❌", color: "#a3402f", bg: "#f7ded9" },
  ];

  return (
    <div>
      <Link to="/" style={styles.linkVolver}>
        ← Regresar al Dashboard
      </Link>
      <h2 style={{ marginBottom: 0 }}>🕘 Histórico de Solicitudes</h2>
      <Ayuda>
        <p style={{ color: "#5b6472", marginTop: "0.25rem", textAlign: "center" }}>
          Solicitudes {textoEntregadas.toLowerCase()} o rechazadas — cuando una solicitud llega a alguno de estos dos
          estados, sale del Dashboard principal y se archiva aquí.
        </p>
      </Ayuda>

      <div className="tarjetas-resumen" style={styles.tarjetas}>
        {TARJETAS.map((t) => (
          <div key={t.texto} style={styles.tarjeta}>
            <div style={{ ...styles.iconoTarjeta, background: t.bg, color: t.color }}>{t.icono}</div>
            <div>
              <div style={styles.tarjetaValor}>
                {t.valor}
                {t.sub && <span style={styles.tarjetaSub}> · {t.sub}</span>}
              </div>
              <div style={styles.tarjetaTexto}>{t.texto}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={styles.card}>
        <div style={styles.pills}>
          {PILLS_ESTADO.map((p) => (
            <button
              key={p.valor}
              type="button"
              onClick={() => cambiarPill(p.valor)}
              style={{ ...styles.pill, ...(pillEstado === p.valor ? styles.pillActivo : {}) }}
            >
              {p.valor === "entregada" ? `✅ ${textoEntregadas}` : p.texto}
            </button>
          ))}
        </div>

        <form onSubmit={buscar} className="barra-filtros" style={styles.filtros}>
          <div style={{ position: "relative" }}>
            <span style={styles.iconoBusqueda}>🔍</span>
            <input
              type="text"
              placeholder="Folio (ej. 004)"
              value={folio}
              onChange={(e) => setFolio(e.target.value)}
              style={{ ...styles.input, paddingLeft: "1.9rem" }}
            />
          </div>
          {esStaff && (
            <input
              type="text"
              placeholder="Nombre del solicitante"
              value={solicitanteNombre}
              onChange={(e) => setSolicitanteNombre(e.target.value)}
              style={styles.input}
            />
          )}
          <label style={styles.campoFecha}>
            Desde
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} style={styles.input} />
          </label>
          <label style={styles.campoFecha}>
            Hasta
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} style={styles.input} />
          </label>
          <button type="submit" style={styles.botonBuscar}>
            🔍 Buscar
          </button>
        </form>

        {error && <p style={{ color: "#a3402f" }}>{error}</p>}

        {cargando ? (
          <Cargando compacto />
        ) : visibles.length === 0 ? (
          <p style={{ marginTop: "0.75rem", color: "#5b6472" }}>
            📭 No hay solicitudes entregadas o rechazadas con esos filtros.
          </p>
        ) : (
          <>
            <div className="table-scroll" style={styles.tablaWrap}>
              <table style={styles.tabla}>
                <thead>
                  <tr style={{ textAlign: "center", borderBottom: "1px solid #d3d9df" }}>
                    <th style={styles.th}>Folio</th>
                    {esStaff && <th style={styles.th}>Solicitante</th>}
                    <th style={styles.th}>Área</th>
                    <th style={styles.th}>Requerimiento</th>
                    <th style={styles.th}>Fecha solicitud</th>
                    <th style={styles.th}>Estado</th>
                    <th style={styles.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {visiblesPagina.map((s, i) => (
                    <tr
                      key={s.id}
                      style={{ borderBottom: "1px solid #eef1f3", background: i % 2 === 1 ? "#fafbfc" : "transparent" }}
                    >
                      <td style={styles.td}>
                        <Link to={`/solicitud/${s.id}`} style={styles.linkFolio}>
                          #{String(s.id).padStart(3, "0")}
                        </Link>
                      </td>
                      {esStaff && (
                        <td style={styles.td}>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                            <span style={styles.avatar}>{iniciales(s.solicitante_nombre)}</span>
                            {s.solicitante_nombre}
                          </div>
                        </td>
                      )}
                      <td style={styles.td}>{s.area_nombre}</td>
                      <td style={styles.tdRequerimiento} title={s.descripcion_requerimiento}>
                        {s.descripcion_requerimiento}
                      </td>
                      <td style={styles.td}>{new Date(s.creado_en).toLocaleDateString("es-NI")}</td>
                      <td style={styles.td}>
                        <Badge
                          estado={estadoVisible(s.estado, s.total_cotizaciones, s.tiene_pendiente_aprobacion, s.en_atencion, s.tiene_pago_pendiente, s.tiene_pago_realizado)}
                          mapa={mapaEstadosSolicitud()}
                        />
                      </td>
                      <td style={styles.td}>
                        <Link to={`/solicitud/${s.id}`} style={styles.botonVer}>
                          Ver detalle
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={styles.pieTabla}>
              <span style={{ fontSize: "0.8rem", color: "#5b6472" }}>
                Mostrando {(paginaSegura - 1) * (tamanoPagina === "todas" ? visibles.length : Number(tamanoPagina)) + 1}
                {" a "}
                {Math.min(
                  paginaSegura * (tamanoPagina === "todas" ? visibles.length : Number(tamanoPagina)),
                  visibles.length
                )}
                {" de "}
                {visibles.length}
              </span>

              <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                {totalPaginas > 1 && (
                  <div style={{ display: "flex", gap: "0.3rem" }}>
                    <button
                      type="button"
                      disabled={paginaSegura === 1}
                      onClick={() => setPagina(paginaSegura - 1)}
                      style={styles.botonPagina}
                    >
                      ‹
                    </button>
                    {Array.from({ length: totalPaginas }, (_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setPagina(i + 1)}
                        style={{ ...styles.botonPagina, ...(paginaSegura === i + 1 ? styles.botonPaginaActivo : {}) }}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      type="button"
                      disabled={paginaSegura === totalPaginas}
                      onClick={() => setPagina(paginaSegura + 1)}
                      style={styles.botonPagina}
                    >
                      ›
                    </button>
                  </div>
                )}
                <label style={{ fontSize: "0.8rem", color: "#5b6472", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  Por página:
                  <select
                    value={tamanoPagina}
                    onChange={(e) => {
                      setTamanoPagina(e.target.value);
                      setPagina(1);
                    }}
                    style={{ ...styles.input, padding: "0.3rem 0.4rem" }}
                  >
                    {TAMANOS_PAGINA.map((t) => (
                      <option key={t} value={t}>
                        {t === "todas" ? "Todas" : t}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  tarjetas: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "0.75rem",
    marginTop: "1.1rem",
  },
  tarjeta: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    background: "#fff",
    border: "1px solid #e2e6ea",
    borderRadius: 12,
    padding: "0.9rem 1rem",
    boxShadow: "0 1px 3px rgba(27,42,74,0.05)",
  },
  iconoTarjeta: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.2rem",
    flexShrink: 0,
  },
  tarjetaValor: { fontSize: "1.4rem", fontWeight: 700, color: "var(--tema-titulo, #0b3c60)", fontVariantNumeric: "tabular-nums" },
  tarjetaSub: { fontSize: "0.85rem", fontWeight: 600, color: "#8993a1" },
  tarjetaTexto: { fontSize: "0.78rem", color: "#5b6472", fontWeight: 600 },
  card: {
    border: "1px solid #e2e6ea",
    borderRadius: 14,
    padding: "1.25rem 1.4rem",
    marginTop: "1.1rem",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
  },
  linkVolver: {
    display: "inline-block",
    color: "var(--tema-primario, #2f6690)",
    fontSize: "0.85rem",
    fontWeight: 600,
    textDecoration: "none",
    marginBottom: "0.75rem",
  },
  pills: {
    display: "flex",
    gap: "0.5rem",
    marginBottom: "1rem",
  },
  pill: {
    border: "1px solid #d3d9df",
    background: "#fff",
    color: "#5b6472",
    borderRadius: 999,
    padding: "0.4rem 0.9rem",
    fontSize: "0.82rem",
    fontWeight: 600,
    cursor: "pointer",
  },
  pillActivo: {
    background: "var(--tema-titulo, #0b3c60)",
    borderColor: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
  },
  filtros: {
    display: "flex",
    gap: "0.6rem",
    flexWrap: "wrap",
    alignItems: "end",
    marginBottom: "1.25rem",
    paddingBottom: "1.1rem",
    borderBottom: "1px solid #eef1f3",
  },
  input: {
    padding: "0.5rem",
    border: "1px solid #d3d9df",
    borderRadius: 6,
    fontFamily: "inherit",
    fontSize: "0.85rem",
  },
  iconoBusqueda: {
    position: "absolute",
    left: "0.55rem",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "0.8rem",
    pointerEvents: "none",
  },
  campoFecha: { display: "flex", flexDirection: "column", gap: "0.2rem", fontSize: "0.8rem" },
  botonBuscar: {
    background: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "0.5rem 1rem",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
  },
  linkFolio: { color: "var(--tema-primario, #2f6690)", fontWeight: 600, textDecoration: "none" },
  botonVer: {
    display: "inline-block",
    border: "1px solid var(--tema-primario, #2f6690)",
    borderRadius: 8,
    padding: "0.35rem 0.8rem",
    fontSize: "0.8rem",
    fontWeight: 600,
    color: "var(--tema-primario, #2f6690)",
    textDecoration: "none",
    whiteSpace: "nowrap",
  },
  tablaWrap: {
    border: "1px solid #eef1f3",
    borderRadius: 10,
  },
  tabla: { width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" },
  th: {
    padding: "0.6rem 0.5rem",
    background: "#eef1f3",
    border: "1px solid #d3d9df",
    fontSize: "0.78rem",
    color: "var(--tema-titulo, #0b3c60)",
  },
  td: { padding: "0.55rem 0.5rem", textAlign: "left" },
  tdRequerimiento: {
    padding: "0.55rem 0.5rem",
    maxWidth: 300,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: "left",
  },
  avatar: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    background: "#3d5a99",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.68rem",
    fontWeight: 700,
    flexShrink: 0,
  },
  pieTabla: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "0.75rem",
    marginTop: "0.9rem",
  },
  botonPagina: {
    border: "1px solid #d3d9df",
    background: "#fff",
    color: "var(--tema-titulo, #0b3c60)",
    borderRadius: 6,
    padding: "0.3rem 0.6rem",
    fontSize: "0.8rem",
    fontWeight: 600,
    cursor: "pointer",
    minWidth: 30,
  },
  botonPaginaActivo: {
    background: "var(--tema-titulo, #0b3c60)",
    borderColor: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
  },
};

export default Historico;
